<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Sent Items">

    </PageHeader>
    
    <div class="container">
      <!-- List -->
      <div class="card">
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="flex flex-col space-y-3 md:space-y-0 md:flex-row md:space-x-3">
            <FilterDropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="created_at" class="form-label">Date Range</label>
                <DatePicker color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.created_at">
                  <template v-slot="{ inputValue, inputEvents }">
                    <div class="flex justify-center">
                      <input
                        :value="inputValue.start"
                        v-on="inputEvents.start"
                        class="form-input prepend"
                      />
                      <div class="flex items-center justify-center flex-none w-10 text-gray-500 border-t border-b border-gray-300">
                        &rarr;
                      </div>
                      <input
                        :value="inputValue.end"
                        v-on="inputEvents.end"
                        class="form-input append"
                      />
                    </div>
                  </template>
                </DatePicker>
              </div>
            </FilterDropdown>
            <SortDropdown :options="sortOptions" />
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- Empty -->
        <div v-else-if="logs.meta.total < 1 && !$route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No notifications have been sent yet.
          </div>
          <div class="text-gray-500">
            Any time an notification is sent it will be logged here.
          </div>
          <button class="mt-4 btn btn-outline" @click="getNotificationLogs()">
            Refresh
          </button>
        </div>

        <!-- No Results -->
        <div v-else-if="logs.meta.total < 1 && $route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No sent items matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see the latest sent notifications.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>

        <!-- Results -->
        <div v-else>
          <!-- Table -->
          <div class="flex flex-col overflow-hidden">
            <div class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div class="inline-block min-w-full overflow-hidden align-middle rounded-t-lg">
                <table class="min-w-full">
                  <thead>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Sent
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Type
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Recipient
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-right text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Delivered
                    </th>
                  </thead>
                  <tbody>
                    <tr v-for="log in logs.data" :key="log.id" class="cursor-pointer hover:bg-gray-50" @click="viewLog(log)">
                      <td class="px-6 py-2 text-gray-500 border-b whitespace-nowrap" >
                        {{ log.created_at | moment('DD MMM HH:mm:ss') }}
                      </td>
                      <td class="px-6 py-2 border-b whitespace-nowrap" >
                        <span class="inline-flex text-xs font-bold tracking-wider uppercase bg-gray-100 rounded-lg">
                          <span class="flex items-center px-2 text-gray-500 bg-gray-200 rounded-l-lg">
                            <fa :icon="['far', 'envelope']" v-if="log.medium === 'email'" />
                            <fa :icon="['far', 'sms']" v-else-if="log.medium === 'sms'" />
                          </span>
                          <span class="px-3 py-1">
                            {{ log.type.replaceAll('-', ' ') }}
                          </span>
                        </span>
                      </td>
                      <td class="px-6 py-2 border-b whitespace-nowrap">
                        {{ log.recipient }} 
                      </td>
                      <td class="px-6 py-2 text-right border-b whitespace-nowrap">
                        <span class="py-1 text-sm text-gray-500 rounded-full" v-if="log.delivered_at">
                          <fa :icon="['far', 'check']" class="mr-1" /> {{ log.delivered_at | moment('HH:mm') }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="logs.meta.total" :per-page="logs.meta.per_page" :current-page="logs.meta.current_page" />
        </div>
      </div>
    </div>

    <Modal :open.sync="showLogModal" title="View Log" size="sm:max-w-lg">
      <div class="grid gap-6 p-6 pb-8" v-if="log">
        <div class="grid grid-cols-2 gap-1">
          <div class="p-2 border-t-2" :class="{ 'border-indigo-600': log.created_at }">
            <div class="text-xs font-bold tracking-wider text-gray-600 uppercase">Sent</div>
            <div>{{ log.created_at | moment('DD MMM HH:mm:ss') }}</div>
          </div>
          <div class="p-2 border-t-2" :class="{ 'border-indigo-600': log.delivered_at }">
            <div class="text-xs font-bold tracking-wider text-gray-600 uppercase">Delivered</div>
            <div>{{ log.delivered_at | moment('DD MMM HH:mm:ss') }}</div>
          </div>
        </div>
        <div class="flex justify-between">
          <div>
            <span class="inline-flex text-xs font-bold tracking-wider text-gray-600 uppercase">
              <span class="flex items-center mr-2 text-base opacity-50">
                <fa :icon="['far', 'envelope']" v-if="log.medium === 'email'" />
                <fa :icon="['far', 'sms']" v-else-if="log.medium === 'sms'" />
              </span>
              <span>
                {{ log.type.replaceAll('-', ' ') }}
              </span>
            </span>
            <div class="-mt-0.5">
              {{ log.recipient }}
            </div>
          </div>
        </div>
        <div>
          <div class="font-bold">{{ log.subject }}</div>
          <div class="text-gray-600">{{ log.contents }}</div>
        </div>
        <div v-if="log.booking_id">
          <router-link :to="{ name: 'BookingEdit', params: { id: log.booking_id } }" class="btn btn-secondary">
            View Booking &rarr;
          </router-link>
        </div>
        <div v-if="log.order_id">
          <router-link :to="{ name: 'OrderEdit', params: { id: log.order_id } }" class="btn btn-secondary">
            View Order &rarr;
          </router-link>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { sub } from 'date-fns'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import FilterDropdown from '@/components/FilterDropdown'
import SortDropdown from '@/components/SortDropdown'

export default {
  name: 'NotificationList',

  components: {
    DatePicker,
    FilterDropdown,
    SortDropdown
  },

  created () {
    this.setFilter()
    this.getNotificationLogs()
  },

  data () {
    return {
      loading: true,
      submitting: false,
      logs: {},
      log: null,
      showLogModal: false,
      filter: {
        created_at: { start: sub(new Date(), { months: 1 }), end: new Date() },
        type: null,
        medium: null,
        recipient: null
      },
      sortOptions: [
        { label: `Time <span class="text-gray-500">(Oldest First)</span>`, prop: 'created_at', dir: 'asc' },
        { label: `Time <span class="text-gray-500">(Newest First)</span>`, prop: 'created_at', dir: 'desc' },
        { label: `Type <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'type', dir: 'asc' },
        { label: `Type <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'type', dir: 'desc' }
      ]
    }
  },

  methods: {
    async getNotificationLogs () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/notification-logs`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.logs = data.logs
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        created_at: { start: sub(new Date(), { months: 1 }), end: new Date() },
        type: null,
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    },

    viewLog (log) {
      this.log = {
        ...log,
        showRawOptions: false,
        showRawEvent: false
      }
      this.showLogModal = true
    },
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getNotificationLogs()
      },
      deep: true
    }
  }
}
</script>